/**
 * Client Intent Utility
 * 
 * Centralized storage for client intent during signup/conversation creation flow.
 * Replaces scattered localStorage keys with a single, structured approach.
 */

const INTENT_KEY = 'clientIntent'
const TARGET_CREATOR_KEY = 'clientTargetCreator'

/**
 * Intent actions:
 * - 'chat': User wants to start chatting with a creator
 * - 'book_session': User wants to book a session with a creator
 * - 'buy_content': User wants to purchase premium content
 * - 'start_call': User wants to start a voice/video call
 * - 'add_funds': User wants to add funds to their wallet
 * - 'browse': User is just browsing (default)
 */

/**
 * Store the user's intent
 * @param {Object} intent - The intent object
 * @param {string} intent.action - 'chat' | 'book_session' | 'buy_content' | 'add_funds' | 'browse'
 * @param {string} [intent.itemId] - For 'buy_content': the content item ID
 * @param {string} [intent.packId] - For 'add_funds': the selected pack ID
 */
export function storeIntent(intent) {
  try {
    localStorage.setItem(INTENT_KEY, JSON.stringify({
      ...intent,
      timestamp: Date.now()
    }))
  } catch (e) {
    console.warn('Failed to store client intent:', e)
  }
}

/**
 * Get the stored intent
 * @returns {Object|null} The intent object or null if not set
 */
export function getIntent() {
  try {
    const stored = localStorage.getItem(INTENT_KEY)
    if (!stored) return null

    const intent = JSON.parse(stored)

    // Expire intents older than 1 hour
    if (intent.timestamp && Date.now() - intent.timestamp > 60 * 60 * 1000) {
      clearIntent()
      return null
    }

    return intent
  } catch (e) {
    console.warn('Failed to get client intent:', e)
    return null
  }
}

/**
 * Clear the stored intent
 */
export function clearIntent() {
  try {
    localStorage.removeItem(INTENT_KEY)
  } catch (e) {
    console.warn('Failed to clear client intent:', e)
  }
}

/**
 * Store target creator data for the intent
 * @param {Object} creatorData - Creator data object
 * @param {string} creatorData._id - Creator's user ID
 * @param {string} [creatorData.username] - Creator's username
 * @param {string} [creatorData.displayname] - Creator's display name
 * @param {string} [creatorData.profileImage] - Creator's profile image URL
 * @param {string} [creatorData.gender] - Creator's gender (for preference inference)
 * @param {Object} [creatorData.onlineStatus] - Creator's online status
 */
export function storeTargetCreator(creatorData) {
  if (!creatorData?._id) {
    console.warn('storeTargetCreator: missing creator ID')
    return
  }

  try {
    localStorage.setItem(TARGET_CREATOR_KEY, JSON.stringify({
      _id: creatorData._id,
      username: creatorData.username,
      // Handle both field name variations
      displayname: creatorData.displayname || creatorData.displayName || creatorData.username,
      profileImage: creatorData.profileImage || creatorData.avatar,
      gender: creatorData.gender,
      onlineStatus: creatorData.onlineStatus,
      // Include call services for preview
      callServices: creatorData.callServices,
      // Session-based services for booking flow
      sessionServices: creatorData.sessionServices,
      videoCallPrice: creatorData.videoCallPrice,
      voiceCallPrice: creatorData.voiceCallPrice,
      // Include message services for paid messaging detection
      messageServices: creatorData.messageServices,
      // Include practices for booking flow
      practices: Array.isArray(creatorData.practices)
        ? creatorData.practices
        : [],
      limits: creatorData.limits && typeof creatorData.limits === 'object'
        ? {
            soft: String(creatorData.limits.soft || '').trim(),
            hard: String(creatorData.limits.hard || '').trim(),
          }
        : { soft: '', hard: '' },
    }))
    console.log(
      'storeTargetCreator: stored',
      creatorData._id,
      creatorData.displayname || creatorData.displayName || creatorData.username,
      'gender:',
      creatorData.gender
    )
  } catch (e) {
    console.warn('Failed to store target creator:', e)
  }
}

/**
 * Get the stored target creator
 * @returns {Object|null} The creator data or null if not set
 */
export function getTargetCreator() {
  try {
    const stored = localStorage.getItem(TARGET_CREATOR_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (e) {
    console.warn('Failed to get target creator:', e)
    return null
  }
}

/**
 * Clear the stored target creator
 */
export function clearTargetCreator() {
  try {
    localStorage.removeItem(TARGET_CREATOR_KEY)
  } catch (e) {
    console.warn('Failed to clear target creator:', e)
  }
}

/**
 * Clear all client intent data (intent + target creator)
 */
export function clearAllIntentData() {
  clearIntent()
  clearTargetCreator()
}


/**
 * Check if user needs to complete setup before conversation
 * @param {Object} userData - User data from auth store
 * @returns {{ needsDisplayname: boolean, needsChatStyle: boolean }}
 */
export function checkSetupNeeded(userData) {
  const displayname = userData?.displayname
  const needsDisplayname = !displayname ||
    displayname === 'Anonymous' ||
    displayname.trim().length < 2

  // Chat-style onboarding is removed from this flow.
  const needsChatStyle = false

  return { needsDisplayname, needsChatStyle }
}
